import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useLabelingSlice from 'redux/slices/hooks/useLabelingSlice'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import useRawLayerAvailable from '../../../utils/useRawLayerAvailable'
import useCellVisualizationUrlParams from '../useCellVisualizationUrlParams'
import { LabelExportButton } from './LabelExportButton'
import { LabelImportButton } from './LabelImportButton'
import { LabelingCoverageDialog } from './LabelingCoverageDialog'
import { LabelingDirtyPrompt } from './LabelingDirtyPrompt'
import { LabelingGrid } from './LabelingGrid'
import { LabelLegend } from './LabelLegend'
import { LabelSelector } from './LabelSelector'
import { findSimilarToLabel } from './findSimilarCells'
import { FALLBACK_LABEL_COLOR, useLabelColors } from './labelColors'
import { useCellEmbeddings } from './useCellEmbeddings'
import { useLabelingEligibility } from './useLabelingEligibility'
import { useLabelingHotkeys } from './useLabelingHotkeys'
import { useLabelingPersistence } from './useLabelingPersistence'
import { useLoadSessionForLabeling } from './useLoadSessionForLabeling'
import { useSessionClusters } from './useSessionClusters'
import { useSessionMorphotypes } from './useSessionMorphotypes'

// Label-state filter for the grid ("Show" control)
const FILTER_ALL = 'all'
const FILTER_UNLABELED = 'unlabeled'
const LABEL_FILTER_PREFIX = 'lbl:'
// Sentinels for the cluster / morphotype source selectors
const ALL_CLUSTERS = '__all_clusters__'
const MORPH_ALL = '__all_morphotypes__'

/**
 * Top-level page for the internal cell-image labeling workflow.
 *
 * Loads the session's cells (the same loader the Cell Vis page uses), groups
 * them by Leiden cluster, and renders the selected cluster's cells as a wall of
 * images. Selection / labeling / export are layered on in later steps.
 */
export const LabelingPage = (): JSX.Element => {
  const handleError = useCallback((error: unknown) => {
    // eslint-disable-next-line no-console
    console.error('Failed to load session for labeling', error)
  }, [])

  // Load the session into the shared Cell Vis store (dataFrame + sessionData)
  // without rewriting the URL (which would navigate off the /label route).
  useLoadSessionForLabeling(handleError)

  const { clusters, cellIdsByCluster, allCellIds, totalCells } = useSessionClusters()
  const morphotypes = useSessionMorphotypes()
  const {
    labeling: { selectedCellIds, labels, sessionLabels },
    invertSelection,
    clearSelection,
    assignLabelToSelection,
    importLabels,
  } = useLabelingSlice()
  const labelColors = useLabelColors()
  const { sessionId } = useCellVisualizationUrlParams()
  const [selectedCluster, setSelectedCluster] = useState<string | undefined>(undefined)
  const [selectedMorphotype, setSelectedMorphotype] = useState<string>(MORPH_ALL)
  const [labelFilter, setLabelFilter] = useState<string>(FILTER_ALL)

  // Raw-crop comparison is internal-only (labeling is already internal-only, so
  // the toggle is always offered here). Whether it is enabled is driven by the
  // run-level availability signal; missing/partial coverage falls back to the
  // masked crop per cell on the API side.
  const {
    cellVisualizations: { sessionData },
  } = useCellVisualizationsSlice()
  const rawLayerAvailable = useRawLayerAvailable(sessionData?.runs?.[0]?.run_id).data === true
  const [showRawCrop, setShowRawCrop] = useState(false)

  // Restore saved labels for this run (or start fresh) and autosave changes.
  // Keyed by run id; sessionId is passed to migrate any legacy session-keyed labels.
  useLabelingPersistence(sessionData?.runs?.[0]?.run_id, sessionId)

  // Coverage notice: when the run exceeds the Cell Vis sampling cap, the session
  // is labelable but does not include every cell. Surface that, persistently.
  const { warning: coverageWarning } = useLabelingEligibility()

  // Default to the first cluster once clusters are available
  useEffect(() => {
    if (!selectedCluster && clusters.length > 0) {
      setSelectedCluster(clusters[0])
    }
  }, [clusters, selectedCluster])

  // Source cells from the cluster selector (a cluster, or all cells)
  const clusterCellIds = useMemo(() => {
    if (selectedCluster === ALL_CLUSTERS) return allCellIds
    return selectedCluster ? cellIdsByCluster.get(selectedCluster) ?? [] : []
  }, [selectedCluster, cellIdsByCluster, allCellIds])

  // Narrow to a morphotype if one is selected (intersection)
  const baseCellIds = useMemo(() => {
    if (selectedMorphotype === MORPH_ALL) return clusterCellIds
    const set = morphotypes.find(
      (morphotype) => String(morphotype.id) === selectedMorphotype
    )?.cellIdSet
    if (!set) return clusterCellIds
    return clusterCellIds.filter((id) => set.has(id))
  }, [clusterCellIds, selectedMorphotype, morphotypes])

  // Apply the label-state filter ("Show") to the base cells
  const visibleCellIds = useMemo(() => {
    if (labelFilter === FILTER_ALL) return baseCellIds
    if (labelFilter === FILTER_UNLABELED) return baseCellIds.filter((id) => !labels[id])
    if (labelFilter.startsWith(LABEL_FILTER_PREFIX)) {
      const target = labelFilter.slice(LABEL_FILTER_PREFIX.length)
      return baseCellIds.filter((id) => labels[id] === target)
    }
    return baseCellIds
  }, [labelFilter, baseCellIds, labels])

  // Embedding-similarity "find more like a label" mode. When active, the grid
  // shows cells ranked by similarity to that label's centroid (session-wide),
  // instead of the current cluster.
  const embeddings = useCellEmbeddings()
  const [similarTo, setSimilarTo] = useState<string | null>(null)
  // Fishing: how many nearest neighbors to surface for labeling (the "next N")
  const [fishCount, setFishCount] = useState(50)
  const similarCellIds = useMemo(
    () => (similarTo ? findSimilarToLabel(embeddings, labels, similarTo, { topK: fishCount }) : null),
    [similarTo, embeddings, labels, fishCount]
  )
  const gridCellIds = useMemo(
    () => (similarTo ? similarCellIds ?? [] : visibleCellIds),
    [similarTo, similarCellIds, visibleCellIds]
  )

  // Label every surfaced neighbor as the seed label in one go. Once labeled they
  // leave the candidate pool, so the grid auto-advances to the next nearest N.
  const labelAllShown = useCallback(() => {
    if (!similarTo || gridCellIds.length === 0) return
    const labeledAt = new Date().toISOString()
    importLabels({
      entries: gridCellIds.map((cellId) => ({ cellId, label: similarTo, labeledAt })),
      mode: 'imported-wins',
    })
  }, [similarTo, gridCellIds, importLabels])

  // Keyboard shortcuts (1-9 assign, i invert, [ ] move clusters)
  const assignLabelByKey = useCallback(
    (label: string) => assignLabelToSelection({ label, labeledAt: new Date().toISOString() }),
    [assignLabelToSelection]
  )
  const invertVisible = useCallback(
    () => invertSelection({ cellIds: gridCellIds }),
    [invertSelection, gridCellIds]
  )
  const goToClusterOffset = useCallback(
    (offset: number) => {
      const index = clusters.indexOf(selectedCluster ?? '')
      const next = index + offset
      if (index >= 0 && next >= 0 && next < clusters.length) {
        setSelectedCluster(clusters[next])
      }
    },
    [clusters, selectedCluster]
  )
  useLabelingHotkeys({
    sessionLabels,
    assignLabel: assignLabelByKey,
    invertVisible,
    prevCluster: () => goToClusterOffset(-1),
    nextCluster: () => goToClusterOffset(1),
  })

  // Count selections within the current view (per the toolbar "N SELECTED")
  const selectedInCluster = useMemo(
    () => baseCellIds.reduce((count, id) => count + (selectedCellIds[id] ? 1 : 0), 0),
    [baseCellIds, selectedCellIds]
  )

  // Running total across all clusters, so it never appears to "reset" on switch
  const totalSelected = useMemo(() => Object.keys(selectedCellIds).length, [selectedCellIds])

  // Running total of labeled cells across all clusters
  const totalLabeled = useMemo(() => Object.keys(labels).length, [labels])

  // Per-cluster selected and labeled counts, surfaced in the dropdown so the
  // user can see which clusters have selections and which are already labeled
  const countsByCluster = useMemo(() => {
    const counts = new Map<string, { selected: number; labeled: number }>()
    clusters.forEach((cluster) => {
      const ids = cellIdsByCluster.get(cluster) ?? []
      let selected = 0
      let labeled = 0
      ids.forEach((id) => {
        if (selectedCellIds[id]) selected += 1
        if (labels[id]) labeled += 1
      })
      counts.set(cluster, { selected, labeled })
    })
    return counts
  }, [clusters, cellIdsByCluster, selectedCellIds, labels])

  // Session still loading
  if (totalCells === 0) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: 'calc(100vh - 120px)' }}
        spacing={2}
      >
        <CircularProgress color="secondary" />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Loading session cells…
        </Typography>
      </Stack>
    )
  }

  // Loaded, but the session has no Leiden clusters to label by
  if (clusters.length === 0) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="body1">
          This session has no Leiden clusters, so there is nothing to label by.
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      {coverageWarning ? (
        <Alert severity="warning" sx={{ borderRadius: 0 }} data-testid="coverage-banner">
          {coverageWarning}
        </Alert>
      ) : null}
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ px: 2, py: 1.5, borderBottom: '1px solid #ececf3' }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Cluster:
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select
            value={selectedCluster ?? ''}
            onChange={(event) => {
              setSelectedCluster(event.target.value)
              setSimilarTo(null)
            }}
            renderValue={(value) => (value === ALL_CLUSTERS ? 'All clusters' : String(value))}
            data-testid="cluster-select"
          >
            <MenuItem value={ALL_CLUSTERS}>All clusters</MenuItem>
            {clusters.map((cluster) => {
              const { selected, labeled } = countsByCluster.get(cluster) ?? {
                selected: 0,
                labeled: 0,
              }
              const badges = [
                selected > 0 ? `${selected} selected` : '',
                labeled > 0 ? `${labeled} labeled` : '',
              ]
                .filter(Boolean)
                .join(' · ')
              return (
                <MenuItem key={cluster} value={cluster}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{ width: '100%', gap: 2 }}
                  >
                    <span>Cluster {cluster}</span>
                    {badges ? (
                      <span style={{ fontSize: 12, color: '#5F55D1' }}>{badges}</span>
                    ) : null}
                  </Stack>
                </MenuItem>
              )
            })}
          </Select>
        </FormControl>
        {morphotypes.length > 0 ? (
          <>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Morphotype:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={selectedMorphotype}
                onChange={(event) => {
                  setSelectedMorphotype(event.target.value)
                  setSimilarTo(null)
                }}
                renderValue={(value) =>
                  value === MORPH_ALL
                    ? 'All'
                    : morphotypes.find((morphotype) => String(morphotype.id) === value)?.name ??
                      'All'
                }
                data-testid="morphotype-select"
              >
                <MenuItem value={MORPH_ALL}>All</MenuItem>
                {morphotypes.map((morphotype) => (
                  <MenuItem key={morphotype.id} value={String(morphotype.id)}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ width: '100%', gap: 2 }}
                    >
                      <span>{morphotype.name}</span>
                      <span style={{ fontSize: 12, color: '#888' }}>
                        {morphotype.cellIds.length.toLocaleString()}
                      </span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        ) : null}
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Show:
        </Typography>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={labelFilter}
            onChange={(event) => setLabelFilter(event.target.value)}
            renderValue={(value) => {
              if (value === FILTER_ALL) return 'All cells'
              if (value === FILTER_UNLABELED) return 'Unlabeled'
              return value.startsWith(LABEL_FILTER_PREFIX)
                ? value.slice(LABEL_FILTER_PREFIX.length)
                : String(value)
            }}
            data-testid="label-filter-select"
          >
            <MenuItem value={FILTER_ALL}>All cells</MenuItem>
            <MenuItem value={FILTER_UNLABELED}>Unlabeled</MenuItem>
            {sessionLabels.map((label) => (
              <MenuItem key={label} value={`${LABEL_FILTER_PREFIX}${label}`}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '3px',
                      backgroundColor: labelColors[label] ?? FALLBACK_LABEL_COLOR,
                      flex: 'none',
                    }}
                  />
                  <span>{label}</span>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip
          title={
            rawLayerAvailable
              ? 'Show the raw (unmasked) crop beside the masked crop'
              : 'No raw crops available for this run.'
          }
        >
          {/* span wrapper so the tooltip still shows while the switch is disabled */}
          <span>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  disabled={!rawLayerAvailable}
                  checked={showRawCrop}
                  onChange={(_event, checked) => setShowRawCrop(checked)}
                  data-testid="labeling-show-raw-crop"
                />
              }
              label="Raw crop"
              labelPlacement="start"
              sx={{ m: 0, flexShrink: 0 }}
            />
          </span>
        </Tooltip>
        <LabelSelector />
        <Typography
          variant="body2"
          sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: '0.06em' }}
          data-testid="selected-count"
        >
          {selectedInCluster.toLocaleString()} SELECTED
          {totalSelected > selectedInCluster ? ` · ${totalSelected.toLocaleString()} TOTAL` : ''}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => invertSelection({ cellIds: gridCellIds })}
          disabled={gridCellIds.length === 0}
        >
          Invert Selections
        </Button>
        <Button
          size="small"
          variant="text"
          onClick={() => clearSelection()}
          disabled={totalSelected === 0}
        >
          Clear
        </Button>
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {labelFilter !== FILTER_ALL ? `${visibleCellIds.length.toLocaleString()} shown · ` : ''}
          {totalLabeled.toLocaleString()} labeled · {baseCellIds.length.toLocaleString()} in view ·{' '}
          {totalCells.toLocaleString()} in session
        </Typography>
        <LabelImportButton />
        <LabelExportButton />
      </Stack>

      <LabelLegend
        canFindSimilar={embeddings.available}
        onFindSimilar={(label) => setSimilarTo(label)}
      />

      {similarTo ? (
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{ px: 2, py: 0.75, backgroundColor: '#f3f1ff', borderBottom: '1px solid #ececf3' }}
        >
          <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
            {gridCellIds.length.toLocaleString()} nearest neighbors of {similarTo}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            (unlabeled, closest first)
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Next
          </Typography>
          <TextField
            type="number"
            size="small"
            value={fishCount}
            onChange={(event) => setFishCount(Math.max(1, Math.min(1000, Number(event.target.value) || 1)))}
            inputProps={{ min: 1, max: 1000, step: 25, 'data-testid': 'fish-count' }}
            sx={{ width: 90, backgroundColor: '#fff' }}
          />
          <Button
            size="small"
            variant="contained"
            onClick={labelAllShown}
            disabled={gridCellIds.length === 0}
            data-testid="fish-label-all"
          >
            Label all as {similarTo}
          </Button>
          <Button size="small" variant="text" onClick={() => setSimilarTo(null)}>
            Exit
          </Button>
        </Stack>
      ) : null}

      <Box sx={{ p: 2, height: 'calc(100vh - 200px)', overflow: 'auto' }}>
        <LabelingGrid cellIds={gridCellIds} showRawCrop={showRawCrop && rawLayerAvailable} />
      </Box>
      <LabelingDirtyPrompt />
      <LabelingCoverageDialog sessionId={sessionId} warning={coverageWarning} />
    </Box>
  )
}

export default LabelingPage
